import React, { useState, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
} from 'react-native';
import SwipeableTabs, { SwipeableTabsRef } from '@/components/SwipeableTabs';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useFocusEffect } from '@react-navigation/native';
import * as Haptics from 'expo-haptics';
import { Svg, Path } from 'react-native-svg';

import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNotifications } from '@/contexts/NotificationContext';
import { apiService } from '@/services/apiService';
import TrailCard, { EloTrail, TrailProgress } from '@/components/TrailCard';
import TrailListItem from '@/components/TrailListItem';
import Avatar from '@/components/Avatar';
import AvatarBlurRing from '@/components/AvatarBlurRing';
import BillBottomNav from '@/components/BillBottomNav';
import { useServerVerifiedPlus } from '@/hooks/useEntitlement';
import TrailStartBottomSheet from '@/components/TrailStartBottomSheet';
import { trailRecentUsersService, TrailRecentUser } from '@/services/trailRecentUsersService';
import CarouselPagination from '@/components/CarouselPagination';
import TrailFlagIcon from '@/components/icons/TrailFlagIcon';

const CARD_WIDTH = 345;
const CARD_GAP = 8;

interface EnrichedTrail extends EloTrail {
  user_progress: TrailProgress & { hasStarted: boolean; startedCount: number };
}

type TabId = 'forYou' | 'iniciadas' | 'explore';

const CategoryBookmarkIcon = () => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.8192 4.39092C12.8192 2.68238 11.6524 2 9.97237 2H6.0279C4.3998 2 3.17969 2.63762 3.17969 4.27676V13.411C3.17904 13.736 3.44239 14 3.76802 14C3.8692 14 3.96845 13.9741 4.05666 13.9247L8.01864 11.7038L11.9436 13.9209C12.227 14.0804 12.5864 13.9805 12.746 13.6971C12.7959 13.6089 12.8225 13.5096 12.8225 13.4078L12.8192 4.39092Z"
      stroke="#3D3C38"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

export default function TrailsHomeScreen() {
  const { t } = useLanguage();
  const router = useRouter();
  const { session, userProfile, sessionReady, user } = useAuth();
  const { unreadCount } = useNotifications();
  const { hasPadrinhoPlus } = useServerVerifiedPlus();

  const [loading, setLoading] = useState(true);
  const [trails, setTrails] = useState<EnrichedTrail[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [startSheetTrail, setStartSheetTrail] = useState<EnrichedTrail | null>(null);
  const [sheetRecentUsers, setSheetRecentUsers] = useState<TrailRecentUser[]>([]);
  const [activeTab, setActiveTab] = useState<TabId>('explore');
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [exploreCarouselIndex, setExploreCarouselIndex] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const carouselRef = useRef<ScrollView>(null);
  const exploreCarouselRef = useRef<ScrollView>(null);
  const swipeRef = useRef<SwipeableTabsRef>(null);

  const TAB_IDS: TabId[] = ['explore', 'forYou', 'iniciadas'];
  const tabToIndex = (tab: TabId) => TAB_IDS.indexOf(tab);
  const indexToTab = (i: number) => TAB_IDS[i];

  const handleTabPress = (tab: TabId) => {
    if (activeTab !== tab) {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      setActiveTab(tab);
    }
  };

  const onRefresh = useCallback(async () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setRefreshing(true);
    try {
      const [trailsResponse, categoriesResponse] = await Promise.all([
        apiService.get('/api/trails'),
        apiService.get('/api/trail/categories'),
      ]);
      if (trailsResponse.success && trailsResponse.data?.trails) {
        setTrails(trailsResponse.data.trails);
      }
      if (categoriesResponse.success && Array.isArray(categoriesResponse.data?.categories)) {
        setCategories(
          categoriesResponse.data.categories.map((c: any) => (typeof c === 'string' ? c : c.name))
        );
      }
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    } catch {
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      if (user && sessionReady && session?.access_token) {
        loadData();
      }
      // C3 (PV2-16): access_token rotates on auto-refresh; sessionReady gate is enough.
    }, [user, sessionReady])
  );

  const loadData = async () => {
    try {
      setLoading(true);
      const [trailsResponse, categoriesResponse] = await Promise.all([
        apiService.get('/api/trails'),
        apiService.get('/api/trail/categories'),
      ]);

      if (trailsResponse.success && trailsResponse.data?.trails) {
        setTrails(trailsResponse.data.trails);
      }

      if (categoriesResponse.success && Array.isArray(categoriesResponse.data?.categories)) {
        setCategories(
          categoriesResponse.data.categories.map((c: any) => (typeof c === 'string' ? c : c.name))
        );
      }
    } catch (error) {
      console.error('Error loading trails:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTrailPress = (trail: EnrichedTrail) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    if (trail.user_progress?.hasStarted) {
      router.push(`/trail/${trail.slug}` as any);
    } else {
      const cached = trailRecentUsersService.getCachedUsers(trail.slug);
      setSheetRecentUsers(cached ?? []);
      setStartSheetTrail(trail);
      if (!cached) {
        trailRecentUsersService.getRecentUsers(trail.slug).then((users) => {
          setSheetRecentUsers(users);
        });
      }
    }
  };

  const handleStartTrail = (trail: EnrichedTrail) => {
    setStartSheetTrail(null);
    router.push(`/trail/${trail.slug}` as any);
  };

  const handleNotificationPress = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    router.push('/notifications' as any);
  };

  const startedTrails = trails.filter(t => t.user_progress?.hasStarted);
  const forYouTrails = [...trails].sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
  const exploreTrails = [...trails].sort((a, b) => a.name.localeCompare(b.name));

  const allCategories = categories;

  const handleCategoryPress = (category: string) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    router.push(`/trail/category/${encodeURIComponent(category)}` as any);
  };

  const newTrails = exploreTrails.slice(0, 5);

  const TABS: { id: TabId; label: string }[] = [
    { id: 'explore', label: t('trail.home.tabs.explore') },
    { id: 'forYou', label: t('trail.home.tabs.forYou') },
    { id: 'iniciadas', label: t('trail.home.tabs.mine') },
  ];

  return (
    <SafeAreaView style={styles.container}>
      {/* Top Header */}
      <View style={styles.topHeader}>
        <TouchableOpacity
          style={styles.avatarButton}
          onPress={() => router.push('/perfil' as any)}
        >
          {hasPadrinhoPlus ? (
            <AvatarBlurRing>
              <Avatar
                uri={userProfile?.avatar_url || null}
                size={32}
                showInitials={true}
                initials={userProfile?.display_name || userProfile?.full_name || 'U'}
                borderWidth={0}
              />
            </AvatarBlurRing>
          ) : (
            <Avatar
              uri={userProfile?.avatar_url || null}
              size={32}
              showInitials={true}
              initials={userProfile?.display_name || userProfile?.full_name || 'U'}
              borderColor="#7E7D75"
              borderWidth={1}
            />
          )}
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('trail.title')}</Text>
        <TouchableOpacity style={styles.notificationButton} onPress={handleNotificationPress} activeOpacity={0.7}>
          <View style={styles.notificationIconContainer}>
            <Svg width="25" height="24" viewBox="0 0 25 24" fill="none">
              <Path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.5 17.8476C18.1392 17.8476 20.7481 17.1242 21 14.2205C21 11.3188 19.1812 11.5054 19.1812 7.94511C19.1812 5.16414 16.5452 2 12.5 2C8.45477 2 5.81885 5.16414 5.81885 7.94511C5.81885 11.5054 4 11.3188 4 14.2205C4.25295 17.1352 6.86177 17.8476 12.5 17.8476Z"
                stroke="#151515"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <Path
                d="M14.8889 20.8572C13.5247 22.372 11.3967 22.3899 10.0195 20.8572"
                stroke="#151515"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </Svg>
            {unreadCount > 0 && (
              <View style={styles.notificationBadge}>
                <Text style={styles.notificationBadgeText}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </Text>
              </View>
            )}
          </View>
        </TouchableOpacity>
      </View>

      {/* Pill Tab Bar */}
      <View style={styles.pillTabBar}>
        {TABS.map(tab => (
          <TouchableOpacity
            key={tab.id}
            style={[styles.pillTab, activeTab === tab.id && styles.pillTabActive]}
            onPress={() => handleTabPress(tab.id)}
            activeOpacity={0.8}
          >
            <Text style={[styles.pillTabText, activeTab === tab.id && styles.pillTabTextActive]} numberOfLines={1}>
              {tab.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Swipeable Content */}
      <SwipeableTabs
        ref={swipeRef}
        activeIndex={tabToIndex(activeTab)}
        onPageSelected={(i) => {
          const tab = indexToTab(i);
          if (tab && tab !== activeTab) {
            Haptics.selectionAsync();
            setActiveTab(tab);
          }
        }}
      >
        {/* Page 0: Explore */}
        <ScrollView
          key="explore"
          style={styles.content}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#669AB7" colors={['#669AB7']} />
          }
        >
          {loading ? (
            <View style={styles.skeletonContainer}>
              <View style={styles.skeletonTitle} />
              <View style={styles.skeletonCarouselCard} />
            </View>
          ) : (
            <>
              {newTrails.length > 0 && (
                <View style={styles.carouselSection}>
                  <Text style={styles.carouselTitle}>{t('trail.home.newTrails')}</Text>
                  <ScrollView
                    ref={exploreCarouselRef}
                    horizontal
                    pagingEnabled={false}
                    showsHorizontalScrollIndicator={false}
                    style={styles.carouselScroll}
                    contentContainerStyle={styles.carouselContainer}
                    decelerationRate="fast"
                    snapToInterval={CARD_WIDTH + CARD_GAP}
                    snapToAlignment="start"
                    onScroll={e => {
                      const idx = Math.round(e.nativeEvent.contentOffset.x / (CARD_WIDTH + CARD_GAP));
                      setExploreCarouselIndex(idx);
                    }}
                    scrollEventThrottle={16}
                  >
                    {newTrails.map(trail => (
                      <View key={trail.slug} style={{ width: CARD_WIDTH, marginRight: CARD_GAP }}>
                        <TrailCard
                          trail={trail}
                          progress={trail.user_progress}
                          onPress={() => handleTrailPress(trail)}
                        />
                      </View>
                    ))}
                  </ScrollView>
                  {newTrails.length > 1 && (
                    <CarouselPagination count={newTrails.length} activeIndex={exploreCarouselIndex} style={{ marginTop: 12 }} />
                  )}
                </View>
              )}
              {allCategories.length > 0 && (
                <View style={styles.categoryChipsSection}>
                  <View style={styles.categoryChipsWrap}>
                    {allCategories.map(category => (
                      <TouchableOpacity
                        key={category}
                        style={styles.categoryChip}
                        onPress={() => handleCategoryPress(category)}
                        activeOpacity={0.7}
                      >
                        <CategoryBookmarkIcon />
                        <Text style={styles.categoryChipText}>
                          {category.charAt(0).toUpperCase() + category.slice(1)}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
              )}
              {exploreTrails.length > 0 && (
                <>
                  <View style={styles.sectionDivider} />
                  <View style={styles.otherTrailsHeader}>
                    <View style={styles.otherTrailsIconBox}>
                      <TrailFlagIcon size={16} color="#3D3C38" />
                    </View>
                    <Text style={styles.otherTrailsTitle}>{t('trail.home.otherTrails')}</Text>
                  </View>
                  <View style={styles.meetingsListSection}>
                    {exploreTrails.map(trail => (
                      <TrailListItem
                        key={trail.slug}
                        trail={trail}
                        progress={trail.user_progress}
                        onPress={() => handleTrailPress(trail)}
                      />
                    ))}
                  </View>
                </>
              )}
              {exploreTrails.length === 0 && (
                <View style={styles.emptyContainer}>
                  <Text style={styles.emptyTitle}>{t('trail.home.noForYou')}</Text>
                </View>
              )}
            </>
          )}
        </ScrollView>

        {/* Page 1: Para você */}
        <ScrollView
          key="forYou"
          style={styles.content}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#669AB7" colors={['#669AB7']} />
          }
        >
          {loading ? (
            <View style={styles.skeletonContainer}>
              <View style={styles.skeletonTitle} />
              <View style={styles.skeletonCarouselCard} />
            </View>
          ) : (
            <>
              {startedTrails.length > 0 && (
                <View style={styles.carouselSection}>
                  <Text style={styles.carouselTitle}>{t('trail.home.continueLearning')}</Text>
                  <ScrollView
                    horizontal
                    pagingEnabled={false}
                    showsHorizontalScrollIndicator={false}
                    style={styles.carouselScroll}
                    contentContainerStyle={styles.carouselContainer}
                    decelerationRate="fast"
                    snapToInterval={CARD_WIDTH + CARD_GAP}
                    snapToAlignment="start"
                    onScroll={e => {
                      const idx = Math.round(e.nativeEvent.contentOffset.x / (CARD_WIDTH + CARD_GAP));
                      setCarouselIndex(idx);
                    }}
                    scrollEventThrottle={16}
                  >
                    {startedTrails.map(trail => (
                      <View key={trail.slug} style={{ width: CARD_WIDTH, marginRight: CARD_GAP }}>
                        <TrailCard
                          trail={trail}
                          progress={trail.user_progress}
                          onPress={() => handleTrailPress(trail)}
                        />
                      </View>
                    ))}
                  </ScrollView>
                  {startedTrails.length > 1 && (
                    <CarouselPagination count={startedTrails.length} activeIndex={carouselIndex} style={{ marginTop: 12 }} />
                  )}
                </View>
              )}
              {forYouTrails.length === 0 ? (
                <View style={styles.emptyContainer}>
                  <Text style={styles.emptyTitle}>{t('trail.home.noForYou')}</Text>
                </View>
              ) : (
                <View style={styles.meetingsListSection}>
                  {forYouTrails.map(trail => (
                    <TrailListItem
                      key={trail.slug}
                      trail={trail}
                      progress={trail.user_progress}
                      onPress={() => handleTrailPress(trail)}
                    />
                  ))}
                </View>
              )}
            </>
          )}
        </ScrollView>

        {/* Page 2: Iniciadas */}
        <ScrollView
          key="iniciadas"
          style={styles.content}
          contentContainerStyle={{ flexGrow: 1 }}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#669AB7" colors={['#669AB7']} />
          }
        >
        {loading ? (
          <View style={styles.skeletonContainer}>
            <View style={styles.skeletonTitle} />
            <View style={styles.skeletonCarouselCard} />
            <View style={styles.meetingsListSection}>
              {[0, 1, 2, 3].map(i => (
                <View key={i} style={styles.skeletonListRow}>
                  <View style={styles.skeletonListIcon} />
                  <View style={styles.skeletonListText}>
                    <View style={styles.skeletonListLine1} />
                    <View style={styles.skeletonListLine2} />
                  </View>
                  <View style={styles.skeletonListChevron} />
                </View>
              ))}
            </View>
          </View>
        ) : (
          <>
            {startedTrails.length > 0 ? (
              <>
                <View style={styles.carouselSection}>
                  <Text style={styles.carouselTitle}>{t('trail.home.continueLearning')}</Text>
                  <ScrollView
                    ref={carouselRef}
                    horizontal
                    pagingEnabled={false}
                    showsHorizontalScrollIndicator={false}
                    style={styles.carouselScroll}
                    contentContainerStyle={styles.carouselContainer}
                    decelerationRate="fast"
                    snapToInterval={CARD_WIDTH + CARD_GAP}
                    snapToAlignment="start"
                    onScroll={e => {
                      const idx = Math.round(e.nativeEvent.contentOffset.x / (CARD_WIDTH + CARD_GAP));
                      setCarouselIndex(idx);
                    }}
                    scrollEventThrottle={16}
                  >
                    {startedTrails.map(trail => (
                      <View key={trail.slug} style={{ width: CARD_WIDTH, marginRight: CARD_GAP }}>
                        <TrailCard
                          trail={trail}
                          progress={trail.user_progress}
                          onPress={() => handleTrailPress(trail)}
                        />
                      </View>
                    ))}
                  </ScrollView>
                  {startedTrails.length > 1 && (
                    <CarouselPagination count={startedTrails.length} activeIndex={carouselIndex} style={{ marginTop: 12 }} />
                  )}
                </View>
                <View style={styles.meetingsListSection}>
                  {startedTrails.map(trail => (
                    <TrailListItem
                      key={trail.slug}
                      trail={trail}
                      progress={trail.user_progress}
                      onPress={() => handleTrailPress(trail)}
                    />
                  ))}
                </View>
              </>
            ) : (
              <View style={styles.trailEmptyState}>
                <View style={styles.trailEmptyIconBox}>
                  <TrailFlagIcon size={20} color="#151515" fill="white" />
                </View>
                <Text style={styles.trailEmptyStateTitle}>{t('trail.home.iniciadas.emptyTitle')}</Text>
                <TouchableOpacity style={styles.trailEmptyStateCTA} onPress={() => handleTabPress('explore')} activeOpacity={0.8}>
                  <Text style={styles.trailEmptyStateCTAText}>{t('trail.home.iniciadas.emptyButton')}</Text>
                </TouchableOpacity>
              </View>
            )}
          </>
        )}
        </ScrollView>
      </SwipeableTabs>

      <BillBottomNav currentRoute="trail" />

      <TrailStartBottomSheet
        trail={startSheetTrail}
        isVisible={startSheetTrail !== null}
        onClose={() => setStartSheetTrail(null)}
        onStart={handleStartTrail}
        recentUsers={sheetRecentUsers}
        hasPadrinhoPlus={hasPadrinhoPlus}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9F8F3',
  },

  /* ── Header ── */
  topHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: 24,
    paddingRight: 20,
    paddingTop: 10,
    paddingBottom: 15,
    backgroundColor: '#F9F8F3',
  },
  avatarButton: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
  },
  notificationButton: {
    padding: 4,
  },
  notificationIconContainer: {
    position: 'relative',
  },
  notificationBadge: {
    position: 'absolute',
    top: -2,
    right: -2,
    backgroundColor: '#E53E3E',
    borderRadius: 10,
    minWidth: 16,
    height: 16,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  notificationBadgeText: {
    fontSize: 10,
    fontFamily: 'Inter_700Bold',
    color: '#FFFFFF',
    textAlign: 'center',
  },

  /* ── Scroll content ── */
  content: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 24,
  },

  /* ── Carousel ── */
  carouselSection: {
    paddingTop: 8,
    paddingBottom: 16,
  },
  carouselTitle: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#7E7D75',
    paddingHorizontal: 24,
    marginBottom: 16,
    lineHeight: 14,
  },
  carouselScroll: {
    flexGrow: 0,
  },
  carouselContainer: {
    paddingHorizontal: 24,
  },

  /* ── Pill Tab Bar ── */
  pillTabBar: {
    flexDirection: 'row',
    backgroundColor: '#F0EEE4',
    borderRadius: 32,
    padding: 4,
    marginHorizontal: 24,
    marginTop: 8,
    marginBottom: 8,
  },
  pillTab: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    height: 40,
    borderRadius: 24,
  },
  pillTabActive: {
    backgroundColor: '#151515',
  },
  pillTabText: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#151515',
  },
  pillTabTextActive: {
    color: '#FFFFFF',
  },

  /* ── Skeleton container ── */
  skeletonContainer: {
    paddingTop: 8,
  },
  meetingsListSection: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 100,
    gap: 16,
  },
  sectionDivider: {
    height: 1,
    backgroundColor: '#F0EEE4',
    marginHorizontal: 0,
  },
  otherTrailsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 24,
    paddingTop: 24,
  },
  otherTrailsIconBox: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F0EEE4',
    justifyContent: 'center',
    alignItems: 'center',
  },
  otherTrailsTitle: {
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
    lineHeight: 19.6,
  },

  /* ── Empty ── */
  emptyContainer: {
    paddingHorizontal: 24,
    paddingTop: 48,
    alignItems: 'center',
    gap: 8,
  },
  emptyTitle: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 14,
    color: '#151515',
    textAlign: 'center',
  },
  emptySubtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: '#7E7D75',
    textAlign: 'center',
    lineHeight: 19.6,
  },
  trailEmptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 16,
    gap: 24,
  },
  trailEmptyIconBox: {
    width: 40,
    height: 40,
    backgroundColor: '#F0EEE4',
    borderRadius: 12,
    padding: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  trailEmptyStateTitle: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    textAlign: 'center',
    lineHeight: 28.8,
    width: 255,
  },
  trailEmptyStateCTA: {
    backgroundColor: '#151515',
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 12,
    height: 40,
    alignSelf: 'center',
    alignItems: 'center',
    justifyContent: 'center',
  },
  trailEmptyStateCTAText: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#FFFFFF',
    lineHeight: 19.6,
  },

  /* ── Skeletons ── */
  skeletonTitle: {
    width: 180,
    height: 20,
    backgroundColor: '#E7E6E0',
    borderRadius: 8,
    marginHorizontal: 24,
    marginBottom: 16,
  },
  skeletonCarouselCard: {
    width: 345,
    height: 206,
    backgroundColor: '#E7E6E0',
    borderRadius: 16,
    marginHorizontal: 24,
  },
  skeletonTab: {
    width: 70,
    height: 20,
    backgroundColor: '#E7E6E0',
    borderRadius: 4,
  },
  skeletonListRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  skeletonListIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#E7E6E0',
  },
  skeletonListText: {
    flex: 1,
    gap: 6,
  },
  skeletonListLine1: {
    width: '70%',
    height: 14,
    backgroundColor: '#E7E6E0',
    borderRadius: 4,
  },
  skeletonListLine2: {
    width: '45%',
    height: 12,
    backgroundColor: '#E7E6E0',
    borderRadius: 4,
  },
  skeletonListChevron: {
    width: 16,
    height: 16,
    backgroundColor: '#E7E6E0',
    borderRadius: 4,
  },

  /* ── Category chips ── */
  categoryChipsSection: {
    paddingHorizontal: 24,
    paddingBottom: 16,
  },
  categoryChipsWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  categoryChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    padding: 8,
    backgroundColor: '#F0EEE4',
    borderRadius: 17,
    borderWidth: 1,
    borderColor: '#DAD8CF',
  },
  categoryChipText: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#3D3C38',
    lineHeight: 16.8,
  },

  /* ── Skeleton chips ── */
  skeletonChipsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: 24,
    paddingBottom: 16,
    gap: 8,
  },
  skeletonChip: {
    height: 32,
    backgroundColor: '#E7E6E0',
    borderRadius: 17,
  },
});
